import { useCallback, useEffect, useMemo, useRef } from 'react';
import { selectAppDomain, useAppSelector } from '@ibk/shared-config';
import { AppDomain } from '@ibk/shared-types';
import { useLazySearchAccountantEmissorContractBeneficiariesQuery, useLazySearchClientEmissorContractBeneficiariesQuery, } from '../services/contractBeneficiariesApi';
const EMPTY_BENEFICIARIES = [];
export function useEmissorContractBeneficiaries({ contractId, productId, searchBody, skip = false, }) {
    const appDomain = useAppSelector(selectAppDomain);
    const isAccountant = appDomain === AppDomain.ESCRITORIO;
    const shouldSkip = skip || !contractId || !productId;
    const [triggerAccountant, accountantState] = useLazySearchAccountantEmissorContractBeneficiariesQuery();
    const [triggerClient, clientState] = useLazySearchClientEmissorContractBeneficiariesQuery();
    const trigger = isAccountant ? triggerAccountant : triggerClient;
    const state = isAccountant ? accountantState : clientState;
    const baseBody = useMemo(() => ({ ...searchBody, productId: productId ?? undefined }), [searchBody, productId]);
    const baseBodyKey = useMemo(() => JSON.stringify(baseBody), [baseBody]);
    const baseBodyRef = useRef(baseBody);
    useEffect(() => {
        baseBodyRef.current = baseBody;
    }, [baseBody]);
    useEffect(() => {
        if (shouldSkip || !contractId) {
            return;
        }
        trigger({ contractId, body: baseBodyRef.current });
    }, [contractId, shouldSkip, baseBodyKey, trigger]);
    const searchSync = useCallback(async (body) => {
        if (!contractId || !productId) {
            return { beneficiaries: EMPTY_BENEFICIARIES, total: 0 };
        }
        const result = await trigger({
            contractId,
            body: { ...body, productId: productId ?? undefined },
        }).unwrap();
        return { beneficiaries: result.result ?? EMPTY_BENEFICIARIES, total: result.total ?? 0 };
    }, [contractId, productId, trigger]);
    const refetch = useCallback(() => {
        if (!contractId || !productId) {
            return;
        }
        trigger({ contractId, body: baseBodyRef.current });
    }, [contractId, productId, trigger]);
    return {
        beneficiaries: state.data?.result ?? EMPTY_BENEFICIARIES,
        total: state.data?.total ?? 0,
        isLoading: state.isLoading || state.isFetching,
        isError: state.isError,
        searchSync,
        refetch,
    };
}
